import React, { useEffect, useMemo, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmt } from '../api.js'

// 법인카드마다 별칭과 부서를 적어 둔다.
//
// 카드는 부서나 사람에게 배정되니, 한 장에 한 번 정해 두면 그 카드로 긁은 것이 통째로 갈린다.
// 부서를 붙이는 가장 싼 방법이라 여기부터 채우는 게 좋다.
//
// 번호가 가려져 오기 때문에(5105*********734) 번호만으로는 어느 카드인지 못 알아본다.
// 카드번호는 가운데가 가려져 오므로 별칭으로 가린다. 사용 실적은 신용카드 화면에서 본다
// - 여기는 카드 그 자체의 설정(유효기간·CVC·한도·비밀번호)을 두는 자리다.
//
// 전체번호는 어디서도 끌어올 수 없다 - 클로브조차 가린 번호만 들고 있다. 그래서 사람이
// 한 번 넣고, 넣은 것은 비밀번호와 같은 자리(서버 파일)로 간다. 목록에는 «설정됨»만
// 내려오고, 「보기」를 눌러 한 장씩 꺼낸다 - 그때마다 누가 언제 봤는지 남는다.
//
// 비밀번호 둘은 DB 에 없다. 서버 파일에만 두고 화면으로는 «설정됨»만 내려온다
// (fa/card_secrets.py). 그래서 백업·내보내기 어디에도 실리지 않는다.
// 클로브 화면에 적어 둔 별칭을 그대로 옮겨 적으면 나중에 대조하기도 쉽다.

// 비밀번호는 서버 파일에만 있고 값이 화면으로 내려오지 않는다. 그래서 «설정됨»만
// 보여 주고, 새로 넣으면 그 값만 위로 보낸다. 비우고 저장하면 지운다.
function 비밀칸({ c, 칸, 있음, save }) {
  const [고치는중, set고치는중] = React.useState(false)
  const [값, set값] = React.useState('')
  if (!고치는중) {
    return (
      <td className="l">
        <span className={있음 ? 'ok' : 'muted'} style={{ fontSize: 12 }}>
          {있음 ? '설정됨' : '없음'}
        </span>
        <button type="button" className="btn secondary tiny" style={{ marginLeft: 6 }}
          onClick={() => { set값(''); set고치는중(true) }}>
          {있음 ? '바꾸기' : '넣기'}
        </button>
      </td>
    )
  }
  const 보내기 = () => { save(c, { [칸]: 값 }); set고치는중(false); set값('') }
  return (
    <td className="l">
      <input type="password" autoFocus value={값} placeholder="비우면 지웁니다"
        style={{ width: 'calc(100% - 52px)' }}
        onChange={(e) => set값(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') 보내기()
          if (e.key === 'Escape') { set고치는중(false); set값('') }
        }} />
      <button type="button" className="btn tiny" onClick={보내기}>저장</button>
      <button type="button" className="btn secondary tiny"
        onClick={() => { set고치는중(false); set값('') }}>취소</button>
    </td>
  )
}

// 전체번호는 목록에 실려 내려오지 않는다. 눌러야 그 한 장만 받아 오고, 20초 뒤 다시
// 가린다 - 화면을 켜 둔 채 자리를 비우는 일이 흔해서다. 받아 온 값은 어디에도 담지 않는다.
function 전체번호칸({ c, save, notify }) {
  const [값, set값] = React.useState('')
  const [고치는중, set고치는중] = React.useState(false)
  const [넣을것, set넣을것] = React.useState('')
  React.useEffect(() => {
    if (!값) return undefined
    const t = setTimeout(() => set값(''), 20000)
    return () => clearTimeout(t)
  }, [값])

  const 보기 = async () => {
    try {
      const r = await portal.post('/api/meta/cards/reveal', { cardNo: c.cardNo })
      set값(r.fullNo || '')
    } catch (e) { notify(e.message) }
  }
  const 보내기 = () => {
    save(c, { fullNo: 넣을것 })
    set고치는중(false); set넣을것(''); set값('')
  }

  if (고치는중) {
    return (
      <td className="l">
        <input autoFocus value={넣을것} placeholder={c.cardNo.replace(/\*/g, '0')}
          inputMode="numeric" style={{ width: 'calc(100% - 52px)', fontVariantNumeric: 'tabular-nums' }}
          onChange={(e) => set넣을것(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') 보내기()
            if (e.key === 'Escape') { set고치는중(false); set넣을것('') }
          }} />
        <button type="button" className="btn tiny" onClick={보내기}>저장</button>
        <button type="button" className="btn secondary tiny"
          onClick={() => { set고치는중(false); set넣을것('') }}>취소</button>
      </td>
    )
  }
  const 열람 = c.마지막열람
  return (
    <td className="l">
      {값 ? (
        <>
          <span style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: 0.3 }}>
            {값.replace(/(.{4})/g, '$1 ').trim()}
          </span>
          <button type="button" className="btn secondary tiny" style={{ marginLeft: 6 }}
            onClick={() => set값('')}>가리기</button>
        </>
      ) : (
        <>
          <span className={c.전체번호있음 ? 'ok' : 'muted'} style={{ fontSize: 12 }}>
            {c.전체번호있음 ? '설정됨' : '없음'}
          </span>
          {c.전체번호있음 && (
            <button type="button" className="btn secondary tiny" style={{ marginLeft: 6 }}
              onClick={보기}>보기</button>
          )}
          <button type="button" className="btn secondary tiny" style={{ marginLeft: 4 }}
            onClick={() => { set넣을것(''); set고치는중(true) }}>
            {c.전체번호있음 ? '바꾸기' : '넣기'}
          </button>
        </>
      )}
      {열람 && (
        <div className="muted" style={{ fontSize: 11 }}>
          마지막 열람 {열람.who || '?'} · {(열람.at || '').slice(0, 16).replace('T', ' ')}
        </div>
      )}
    </td>
  )
}

export default function Cards() {
  const [보임, set보임] = useState({})
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [q, setQ] = useState('')
  const [안붙은것만, set안붙은것만] = useState(false)
  const [drafts, setDrafts] = useState({})
  const [toast, setToast] = useState('')
  const [적용중, set적용중] = useState(false)

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 2600) }

  const load = async () => {
    setErr('')
    try { setData(await portal.get('/api/meta/cards')) }
    catch (e) { setErr(e.message) }
  }
  useEffect(() => { load() }, [])

  const rows = useMemo(() => {
    if (!data) return []
    const 말 = q.trim()
    return data.cards.filter((c) => {
      if (안붙은것만 && c.deptCode) return false
      if (!말) return true
      return c.cardNo.includes(말) || (c.alias || '').includes(말)
        || (c.deptName || '').includes(말) || (c.stores || []).some((s) => s.includes(말))
    })
  }, [data, q, 안붙은것만])

  const 요약 = useMemo(() => {
    if (!data) return { 전체: 0, 붙음: 0, 금액: 0, 안붙은금액: 0 }
    let 붙음 = 0, 금액 = 0, 안붙은금액 = 0
    for (const c of data.cards) {
      금액 += c.amount || 0
      if (c.deptCode) 붙음 += 1
      else 안붙은금액 += c.amount || 0
    }
    return { 전체: data.cards.length, 붙음, 금액, 안붙은금액 }
  }, [data])

  const save = async (c, 바뀐것) => {
    try {
      await portal.post('/api/meta/cards', { cardNo: c.cardNo, ...바뀐것 })
      setDrafts((d) => { const n = { ...d }; delete n[c.cardNo]; return n })
      load()
    } catch (e) { setErr(e.message) }
  }

  const 거래에적용 = async () => {
    set적용중(true)
    try {
      const r = await portal.post('/api/meta/cards/apply', { start: '2026-07-01' })
      notify(`카드 ${r.규칙}장으로 거래 ${fmt(r.채움)}건에 부서를 붙였습니다`)
    } catch (e) { setErr(e.message) } finally { set적용중(false) }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
        <input placeholder="카드번호·별칭·부서·가맹점 검색" style={{ width: 240 }}
          value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="muted" style={{ fontSize: 12, display: 'inline-flex', gap: 5 }}>
          <input type="checkbox" checked={안붙은것만}
            onChange={(e) => set안붙은것만(e.target.checked)} />
          부서 안 붙은 것만
        </label>
        <button className="btn" disabled={적용중 || !요약.붙음} onClick={거래에적용}>
          {적용중 ? '붙이는 중…' : '거래에 부서 붙이기'}
        </button>
        <span className="muted" style={{ marginLeft: 'auto', fontSize: 12 }}>
          카드 {요약.전체}장 중 부서 {요약.붙음}장
          {요약.안붙은금액 > 0 && ` · 부서 없는 사용액 ${fmt(Math.round(요약.안붙은금액))}원`}
        </span>
      </div>

      <div className="hint" style={{ marginBottom: 10 }}>
        카드번호는 카드사가 가운데를 가려서 줍니다 — 클로브에도 가린 번호만 있어
        <b>전체번호는 사람이 한 번 넣어야 합니다.</b> 넣은 번호는 DB 가 아니라 비밀번호와
        같은 자리(서버 파일)에 들어가고, 목록에는 «설정됨»만 내려옵니다.
        <b>보기</b>를 누르면 그 한 장만 20초 동안 보이고, 누가 언제 봤는지 남습니다.
        별칭은 클로브에 적어 둔 것과 같게 쓰면 나중에 대조하기 쉽습니다.
        <br />
        부서를 정한 뒤 <b>거래에 부서 붙이기</b>를 누르면 그 카드로 긁은 건에 한꺼번에 붙습니다.
        사람이 직접 정해 둔 부서는 덮지 않습니다.
      </div>

      {err && <div className="error-box">{err}</div>}
      {!err && !data && <div className="muted">불러오는 중…</div>}

      {data && (
        <div className="panel">
          <table>
            <thead>
              <tr>
                <th className="l" style={{ width: 160 }}>카드번호</th>
                <th className="l" style={{ width: 210 }}>전체번호</th>
                <th className="l" style={{ width: 180 }}>별칭</th>
                <th className="l" style={{ width: 170 }}>부서</th>
                <th className="l" style={{ width: 90 }}>유효기간</th>
                <th className="l" style={{ width: 90 }}>CVC</th>
                <th style={{ width: 120 }}>한도</th>
                <th className="l" style={{ width: 150 }}>비밀번호</th>
                <th className="l" style={{ width: 150 }}>일반결제 비밀번호</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="c muted">해당하는 카드가 없습니다</td></tr>
              )}
              {rows.map((c) => {
                const d = drafts[c.cardNo] || {}
                const alias = d.alias !== undefined ? d.alias : c.alias
                return (
                  <tr key={c.cardNo}>
                    <td className="l">
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{c.cardNo}</span>
                      {c.issuer && <div className="muted" style={{ fontSize: 11 }}>{c.issuer}</div>}
                    </td>
                    <전체번호칸 c={c} save={save} notify={notify} />
                    <td className="l">
                      <input value={alias || ''} placeholder="예: 더에르고사업본부_자사몰MD"
                        style={{ width: '100%' }}
                        onChange={(e) => setDrafts((s) => ({ ...s, [c.cardNo]: { ...d, alias: e.target.value } }))}
                        onBlur={() => { if (d.alias !== undefined && d.alias !== c.alias) save(c, { alias: d.alias }) }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }} />
                    </td>
                    <td className="l">
                      <select value={c.deptCode || ''} style={{ width: '100%' }}
                        onChange={(e) => save(c, { deptCode: e.target.value })}>
                        <option value="">미지정</option>
                        {data.departments.map((x) => (
                          <option key={x.code} value={x.code}>{x.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="l">
                      <input defaultValue={c.expiry || ''} placeholder="MM/YY" maxLength={5}
                        style={{ width: '100%', fontVariantNumeric: 'tabular-nums' }}
                        onBlur={(e) => { if ((e.target.value || '') !== (c.expiry || '')) save(c, { expiry: e.target.value }) }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }} />
                    </td>
                    {/* CVC 는 어깨너머로 보이지 않게 기본은 가린다. 카드번호는 이미 가운데가
                        가려져 있어(카드사가 그렇게 준다) CVC 만으로는 결제에 쓸 수 없다 */}
                    <td className="l">
                      <input type={보임[c.cardNo] ? 'text' : 'password'}
                        defaultValue={c.cvc || ''} placeholder="•••" maxLength={4}
                        style={{ width: 'calc(100% - 26px)', fontVariantNumeric: 'tabular-nums' }}
                        onBlur={(e) => { if ((e.target.value || '') !== (c.cvc || '')) save(c, { cvc: e.target.value }) }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }} />
                      <button type="button" className="btn secondary tiny" style={{ padding: '2px 4px' }}
                        title={보임[c.cardNo] ? '가리기' : '보기'}
                        onClick={() => set보임((s2) => ({ ...s2, [c.cardNo]: !s2[c.cardNo] }))}>
                        {보임[c.cardNo] ? '🙈' : '👁'}
                      </button>
                    </td>
                    <td className="r">
                      <input defaultValue={c.limit ?? ''} placeholder="예: 5000000"
                        style={{ width: '100%', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                        onBlur={(e) => {
                          const v = (e.target.value || '').replace(/[^\d]/g, '')
                          if (String(c.limit ?? '') !== v) save(c, { limit: v })
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }} />
                    </td>
                    <비밀칸 c={c} 칸="password" 있음={c.비밀번호있음} save={save} />
                    <비밀칸 c={c} 칸="payPassword" 있음={c.일반결제비밀번호있음} save={save} />
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
